import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { isDefined } from '@trimble-gcs/common';
import { ErrorState } from '../error-handling/error.state';
import { SetInfoView } from '../options-panel/options-panel.actions';
import { OptionsPanelInfoView } from '../options-panel/options-panel.models';
import { SortInfo } from '../scandata/scandata-query.models';
import { sortScandata } from '../scandata/scandata-sort';
import { SelectOnly, SetSelected, SetSortInfo } from '../scandata/scandata.actions';
import { ScandataModel } from '../scandata/scandata.models';
import { ScandataState } from '../scandata/scandata.state';
import {
  ScandataEmptyComponent,
  ScandataEmptyReason,
} from './scandata-empty/scandata-empty.component';
import { scandataListTranslations } from './scandata-list.translations';
import { ScandataTableComponent } from './scandata-table/scandata-table.component';

@UntilDestroy()
@Component({
  imports: [
    CommonModule,
    ScandataTableComponent,
    ScandataEmptyComponent,
    MatProgressBarModule,
    ScrollingModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './scandata-list.component.html',
  styleUrls: ['./scandata-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScandataListComponent {
  private readonly store = inject(Store);

  readonly translations = scandataListTranslations();

  readonly isLoading = this.store.selectSignal(ScandataState.isLoading);
  readonly scanLoadError = this.store.selectSignal(ErrorState.hasError('scanLoadError'));
  readonly sortInfo = this.store.selectSignal(ScandataState.sortInfo);

  readonly scandataList = computed(() => {
    const data = this.textFilteredScandata();
    const sort = this.sortInfo();
    return sortScandata(data, sort.sortBy, sort.sortDirection);
  });

  private readonly textFilteredScandata = this.store.selectSignal(
    ScandataState.textFilteredScandata,
  );

  private readonly textFilter = this.store.selectSignal(ScandataState.textFilter);
  private readonly scandata = this.store.selectSignal(ScandataState.scandata);
  private readonly filterCount = this.store.selectSignal(ScandataState.filterCount);

  readonly showScandataEmpty = computed(() => {
    const isLoading = this.isLoading();
    const scanLoadError = this.scanLoadError();
    const scandataList = this.scandataList();
    return isLoading || scanLoadError ? false : scandataList.length === 0;
  });

  readonly scandataEmptyReason = computed(() => {
    const filterCount = this.filterCount();
    const scandata = this.scandata();
    const textFilter = this.textFilter();

    if (filterCount === 0 && scandata.length === 0) {
      return ScandataEmptyReason.NoUploads;
    }

    if (filterCount > 0 || isDefined(textFilter)) {
      return ScandataEmptyReason.NoFilterResults;
    }

    return ScandataEmptyReason.NoUploads;
  });

  selectionChange(scans: ScandataModel[]) {
    // Get the currently visible scans after applying the text filter
    const visibleScans = this.textFilteredScandata();

    // Identify selected scans that are currently filtered out (not visible)
    const hiddenSelectedScans = this.scandata()
      .filter((scan) => {
        const isHidden = !visibleScans.some((visibleScan) => visibleScan.id === scan.id);
        return isHidden && scan.selected;
      })
      .map((scan) => ({ ...scan, selected: false }));

    // Combine new selections with deselected hidden scans
    const updatedSelections = [...scans, ...hiddenSelectedScans];

    this.store.dispatch(new SetSelected(updatedSelections));
  }

  chipClick(model: ScandataModel) {
    this.store.dispatch([
      new SetInfoView(OptionsPanelInfoView.Tagging),
      new SelectOnly([model.id]),
    ]);
  }

  sortInfoChange(sortInfo: SortInfo) {
    this.store.dispatch(new SetSortInfo(sortInfo));
  }
}
